import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { reviewsApi, moviesApi, languageToTMDB, getGenreNames } from '../services/api';
import { ArrowRight, Clock, Calendar } from 'lucide-react';

const MovieCard = ({ movie, review, useTranslatedOverview }) => {
  const genres = movie.genres 
    ? movie.genres.map(g => g.name) 
    : getGenreNames(movie.genre_ids || []);

  // Use TMDB overview for non-English languages if available
  const excerpt = useTranslatedOverview && movie.overview 
    ? movie.overview 
    : (review?.excerpt || movie.overview);

  return (
    <Link
      to={review ? `/review/${review.id}` : `/movie/${movie.id}`}
      className="group block bg-[#121917] border border-[#52ea88]/10 overflow-hidden hover:border-[#52ea88]/40 transition-all duration-400"
    >
      {/* Poster Image */}
      <div className="relative aspect-[2/3] overflow-hidden">
        {movie.poster ? (
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-[#0B0F0E] flex items-center justify-center">
            <span className="text-[#9FBFB4]/50">No Image</span>
          </div>
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#121917] via-transparent to-transparent" />
        
        {/* Genre Tags */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {genres.slice(0, 2).map((g) => (
            <span
              key={g}
              className="px-2 py-1 bg-[#0B0F0E]/80 backdrop-blur-sm text-[#52ea88] text-xs font-medium"
            >
              {g}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Title & Year */}
        <div className="space-y-1">
          <h3 className="text-xl font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors line-clamp-1">
            {movie.title}
          </h3>
          <div className="flex items-center gap-4 text-[#9FBFB4] text-sm">
            {movie.year && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {movie.year}
              </span>
            )}
            {movie.runtime && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {movie.runtime} min
              </span>
            )}
          </div>
        </div>

        {/* Excerpt */}
        <p className="text-[#9FBFB4]/80 text-sm line-clamp-2 leading-relaxed">
          {excerpt}
        </p>

        {/* Director */}
        {movie.director && (
          <p className="text-[#9FBFB4] text-sm">
            <span className="text-[#52ea88]">Dir.</span> {movie.director}
          </p>
        )}
      </div>
    </Link>
  );
};

const FeaturedReviews = () => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [movieDetails, setMovieDetails] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tmdbLang = languageToTMDB[language] || 'en-US';
        
        // Fetch featured reviews
        const reviewsData = await reviewsApi.getFeatured(4);
        setReviews(reviewsData);

        // Fetch movie details for each review
        // Always fetch English first to get the original poster, then translated text
        const detailsArray = await Promise.all(
          reviewsData.map(async (review) => {
            try {
              // Always fetch English version first for the original poster
              const englishDetail = await moviesApi.getDetail(review.tmdb_id, 'en-US');
              const englishPoster = englishDetail.poster;
              
              // If not English, also fetch translated version for text
              let movieDetail = englishDetail;
              if (tmdbLang !== 'en-US') {
                const translatedDetail = await moviesApi.getDetail(review.tmdb_id, tmdbLang);
                // Use translated text but keep the original English poster
                movieDetail = {
                  ...translatedDetail,
                  poster: englishPoster // Always use English poster
                };
              }
              
              return { tmdbId: review.tmdb_id, detail: movieDetail };
            } catch (err) {
              console.error(`Error fetching movie ${review.tmdb_id}:`, err);
              return { tmdbId: review.tmdb_id, detail: null };
            }
          })
        );
        
        // Convert array to object
        const details = {};
        detailsArray.forEach(item => {
          if (item.detail) {
            details[item.tmdbId] = item.detail;
          }
        });
        
        setMovieDetails(details);
      } catch (error) {
        console.error('Error fetching featured reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  if (loading) {
    return (
      <section className="bg-[#0B0F0E] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#52ea88] border-t-transparent animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#0B0F0E] py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#E6FFF6]">
              {t('sections.featured')}
            </h2>
            <div className="w-20 h-1 bg-[#52ea88]"></div>
          </div>
          <Link
            to="/reviews"
            className="group flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors"
          >
            <span>{t('common.viewAll')}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Movie Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((review) => {
            const movie = movieDetails[review.tmdb_id] || { title: review.title };
            // Use translated overview for non-English languages
            const useTranslatedOverview = language !== 'en';
            return (
              <MovieCard 
                key={review.id} 
                movie={movie} 
                review={review} 
                useTranslatedOverview={useTranslatedOverview}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedReviews;
